// Guided starter canvases. Pure data: each template's build(lang) returns
// { name, nodes, edges } using the canvas node types (resizableText headers +
// postItNode prompt stickies). onNodeUpdate is re-attached on load (never here).
// Notes are positioned BELOW the card deck (y >= 700) so they don't overlap it.
//
// Content is keyed by language (it/en), same pattern as src/data/testimonials.js:
// pick [lang] || .it. Italian content is byte-identical to the pre-i18n version.

// Positioned in the "Lavagna" work area (below the Card Deck, above the
// Brainstorming zones — see ZoneLayer), so they never overlap the deck.
const COLS = [40, 320, 600, 880, 1160];
const Y_HEADER = 2050;
const Y_STICKY = 2122;
const STICKY_BG = ['#FFF1C2', '#D9F2E6', '#E5E9FF', '#FFE0D6', '#EDE3FF'];

function header(tplId, col, text) {
  const id = `${tplId}-h${col}`;
  return {
    id,
    type: 'resizableText',
    position: { x: COLS[col], y: Y_HEADER },
    data: { label: text, id, group: 'note' },
  };
}

function sticky(tplId, col, row, text) {
  const id = `${tplId}-s${col}-${row}`;
  return {
    id,
    type: 'postItNode',
    position: { x: COLS[col], y: Y_STICKY + row * 138 },
    data: { label: text, id, group: 'note', w: 210, h: 122, bg: STICKY_BG[col % STICKY_BG.length] },
  };
}

// content: { it: { title, description, columns }, en: { title, description, columns } }
// columns: [{ head, prompts: [string, ...] }]
function makeTemplate(id, content) {
  return {
    id,
    build(lang) {
      const c = content[lang] || content.it;
      const nodes = [];
      c.columns.forEach((col, i) => {
        nodes.push(header(id, i, col.head));
        col.prompts.forEach((p, r) => nodes.push(sticky(id, i, r, p)));
      });
      return { name: c.title, nodes, edges: [] };
    },
    title(lang) {
      return (content[lang] || content.it).title;
    },
    description(lang) {
      return (content[lang] || content.it).description;
    },
  };
}

export const CANVAS_TEMPLATES = [
  makeTemplate('nuovo-prodotto-ai', {
    it: {
      title: 'Nuovo prodotto AI da zero',
      description: 'Dall’idea grezza a un concept di prodotto AI: problema, utenti, capacità, valore, rischi.',
      columns: [
        { head: 'Problema', prompts: ['Qual è il problema reale da risolvere?', 'Perché è importante adesso?'] },
        { head: 'Utenti', prompts: ['Chi sono gli utenti?', 'Quale bisogno o frustrazione hanno?'] },
        { head: 'Capacità AI', prompts: ['Quale capacità AI lo abilita? (generazione, predizione, classificazione…)'] },
        { head: 'Valore', prompts: ['Qual è il valore unico vs le alternative?'] },
        { head: 'Rischi', prompts: ['Rischi, bias o limiti etici da gestire?'] },
      ],
    },
    en: {
      title: 'New AI product from scratch',
      description: 'From raw idea to an AI product concept: problem, users, capability, value, risks.',
      columns: [
        { head: 'Problem', prompts: ['What is the real problem to solve?', 'Why does it matter now?'] },
        { head: 'Users', prompts: ['Who are the users?', 'What need or frustration do they have?'] },
        { head: 'AI capability', prompts: ['Which AI capability enables it? (generation, prediction, classification…)'] },
        { head: 'Value', prompts: ['What is the unique value vs. the alternatives?'] },
        { head: 'Risks', prompts: ['Risks, bias, or ethical limits to manage?'] },
      ],
    },
  }),
  makeTemplate('servizio-potenziato-ai', {
    it: {
      title: 'Servizio esistente → potenziato con AI',
      description: 'Reinventa un servizio che già offri introducendo l’AI nei punti giusti.',
      columns: [
        { head: 'Servizio attuale', prompts: ['Com’è il servizio oggi?'] },
        { head: 'Punti deboli', prompts: ['Dove è lento, costoso o frustrante?'] },
        { head: 'Dove l’AI aiuta', prompts: ['Quali passi può accelerare o migliorare l’AI?'] },
        { head: 'Nuova esperienza', prompts: ['Come cambia l’esperienza per l’utente?'] },
        { head: 'Metriche', prompts: ['Come misuri il miglioramento?'] },
      ],
    },
    en: {
      title: 'Existing service → AI-enhanced',
      description: 'Reinvent a service you already offer by introducing AI at the right points.',
      columns: [
        { head: 'Current service', prompts: ['What does the service look like today?'] },
        { head: 'Weak points', prompts: ['Where is it slow, costly, or frustrating?'] },
        { head: 'Where AI helps', prompts: ['Which steps can AI speed up or improve?'] },
        { head: 'New experience', prompts: ['How does the experience change for the user?'] },
        { head: 'Metrics', prompts: ['How do you measure the improvement?'] },
      ],
    },
  }),
  makeTemplate('valida-idea-ai', {
    it: {
      title: 'Valida un’idea AI',
      description: 'Metti alla prova un’ipotesi prima di costruire: evidenze, rischi, esperimento, metriche.',
      columns: [
        { head: 'Ipotesi', prompts: ['Cosa credi sia vero? (utente/problema/soluzione)'] },
        { head: 'Evidenze', prompts: ['Quali prove hai già? Cosa manca?'] },
        { head: 'Rischi & etica', prompts: ['Quali rischi, bias o impatti vanno valutati?'] },
        { head: 'Esperimento', prompts: ['Qual è il test più piccolo per imparare?'] },
        { head: 'Metriche', prompts: ['Cosa misuri per dire “funziona”?'] },
      ],
    },
    en: {
      title: 'Validate an AI idea',
      description: 'Put a hypothesis to the test before building: evidence, risks, experiment, metrics.',
      columns: [
        { head: 'Hypothesis', prompts: ['What do you believe is true? (user/problem/solution)'] },
        { head: 'Evidence', prompts: ['What proof do you already have? What is missing?'] },
        { head: 'Risks & ethics', prompts: ['Which risks, bias, or impacts need assessing?'] },
        { head: 'Experiment', prompts: ['What is the smallest test to learn from?'] },
        { head: 'Metrics', prompts: ['What do you measure to say "it works"?'] },
      ],
    },
  }),
  makeTemplate('modello-business-ai', {
    it: {
      title: 'Modello di business AI',
      description: 'Schizza il modello economico di un prodotto/servizio AI.',
      columns: [
        { head: 'Segmento', prompts: ['A chi ti rivolgi?'] },
        { head: 'Proposta di valore', prompts: ['Perché ti scelgono?'] },
        { head: 'Canali', prompts: ['Come raggiungi e servi gli utenti?'] },
        { head: 'Ricavi', prompts: ['Come generi ricavi? (una tantum, licenza, uso…)'] },
        { head: 'Costi AI', prompts: ['Costi di modello, dati, inferenza?'] },
      ],
    },
    en: {
      title: 'AI business model',
      description: 'Sketch the economic model of an AI product/service.',
      columns: [
        { head: 'Segment', prompts: ['Who are you targeting?'] },
        { head: 'Value proposition', prompts: ['Why do they choose you?'] },
        { head: 'Channels', prompts: ['How do you reach and serve users?'] },
        { head: 'Revenue', prompts: ['How do you generate revenue? (one-off, license, usage…)'] },
        { head: 'AI costs', prompts: ['Model, data, inference costs?'] },
      ],
    },
  }),
];
